/**
 * DirectorySalesPage.jsx
 * ──────────────────────
 * Public sales page for hustledirectory.au (rendered by RootRouter on the
 * bare domain) AND surfaced from the HustlePlate bigmenu under
 * "Grow your reach" → HustleDirectory.
 *
 * Audience: restaurant / café / venue owners who want to CLAIM their
 * existing listing (ABN-verified) or ADD a new premises. The listing
 * directory itself continues to live at `/directory` (public search) and
 * `/directory/au` (the state-tile landing page — untouched).
 *
 * Visual system matches HustlePlateSalesPage:
 *   • Cream `#fdf9ec` background, Georgia serif headings
 *   • Amber/emerald accents, rounded pill CTAs
 *   • Prints to PDF cleanly (window.print() button)
 *   • PlatformHeader + SuiteFooter for cross-module chrome
 *
 * Data:
 *   • Live entity / suburb / state counts fetched from
 *     GET /api/directory/au/overview (same endpoint the state grid uses).
 *
 * Post-signup routing:
 *   • Primary CTA → /add-business (unified BusinessSignup manifest_id="plugin_au")
 *   • Secondary  → /claim-business  (same page, "isClaim=true" hero copy)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Printer, ArrowRight, Check, Sparkles, MapPin, Utensils, Clock,
  ShieldCheck, Wine, Star, Search, Building2, Phone, Camera, Globe,
  BadgeCheck, Zap, Users, TrendingUp,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FounderChatPill from "@/components/FounderChatPill";
import DirectoryFreshStrip from "@/components/DirectoryFreshStrip";
const LOGO =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";

// ── Design tokens (mirror HustlePlateSalesPage) ─────────────────────
const heroStyle = {
  padding: "40px 0 24px",
};
const ctaPrimaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999, border: "none",
  background: "#c2410c", color: "#fff",
  fontWeight: 700, fontSize: 15, cursor: "pointer",
  boxShadow: "0 8px 20px rgba(194,65,12,.22)",
  textDecoration: "none",
};
const ctaSecondaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999,
  border: "1.5px solid #1a1408", color: "#1a1408",
  fontWeight: 700, fontSize: 15,
  background: "transparent", textDecoration: "none",
};

// ── Tier cards (mirrors the HustlePlate tier grid look) ─────────────
const TIERS = [
 {key:"free",name:"Business listing",price:"Find your listing",period:"",color:"#1f7a1f",
  blurb:"Start with the information already recorded for your business. Check it and request access to keep it useful.",
  cta:{label:"Find my business",href:"/directory"},
  features:["Public business profile","Name, category and location where recorded","Phone and website links where available","Search by business, category, suburb or postcode","Structured business information and discovery sitemaps"]},
 {key:"featured",name:"More for your business",price:"Ask about options",period:"",color:"#b08a3e",highlight:true,
  blurb:"Available tools and upgrades depend on your account and business. Confirm the features and price before choosing a paid service.",
  cta:{label:"Claim my listing",href:"/claim-business"},
  features:["Maintain your business details after ownership checks","Photos and service information where supported","Protected enquiry forms","Discuss enhanced presentation and connected tools","No promise of rankings, traffic or customer numbers"]}
];
const PUBLIC_UX = [
 {icon:MapPin,title:"Location and contact details",body:"Help visitors find your recorded address, suburb, postcode and contact information. Details are shown when available."},
 {icon:Search,title:"Useful local search",body:"Search by name or category, then narrow by suburb or postcode. Autocomplete suggests choices that have matching listings."},
 {icon:Star,title:"Ratings where available",body:"Compare recorded ratings and review counts. Missing ratings are shown as missing, rather than invented."},
 {icon:Camera,title:"Business photos",body:"View the images available on a business profile. Owners can discuss updating their listing and presentation."},
 {icon:Globe,title:"Website and services",body:"Open a business website or read its public service pages where those details have been supplied."},
 {icon:ShieldCheck,title:"Protected enquiries",body:"Send an enquiry through the available contact form. Business details and ownership status should still be checked when making a decision."}
];
const STEPS = [
 {n:"01",title:"Search for your business",body:"Search the live directory by name, category, suburb or postcode to find an existing record.",icon:Search},
 {n:"02",title:"Request access or add a business",body:"Claim an existing listing or submit a missing business. Ownership checks apply; an ABN lookup alone does not prove control of a business.",icon:BadgeCheck},
 {n:"03",title:"Keep the details current",body:"After access is approved, maintain the details supported by your account. Confirm available tools before purchasing upgrades.",icon:Zap}
];
const FAQS = [
 {q:"Are all listings claimed or verified?",a:"No. The directory includes imported public records as well as maintained listings. The listing total is not a count of verified owners, paying customers or independently inspected businesses."},
 {q:"What do the totals mean?",a:"Listings counts active, named public inventory records with a recorded state field. Suburbs geocoded counts suburb records with valid numeric coordinates. Some business records still need details completed. Totals come from a maintained database summary, not a full count on every visit."},
 {q:"What if my business is missing?",a:"Use Add a business to begin submitting your details. Checks and publication steps apply; we do not promise an instant publication time."},
 {q:"Does a listing guarantee Google rankings or enquiries?",a:"No. Public pages and sitemaps help discovery, but search engines decide whether to index or rank a page. Customer enquiries and sales are not guaranteed."},
 {q:"Can I correct inaccurate details?",a:"Find the listing and request access or contact the directory about a correction. Only submit information you can substantiate."},
 {q:"What do upgrades cost?",a:"Confirm the current features, eligibility and price through your account or a quote before purchasing. This page does not promise an unverified plan or price."}
];

// ── Card renderers ──────────────────────────────────────────────────
function TierCard({ tier }) {
  return (
    <div
      data-testid={`ds-tier-${tier.key}`}
      style={{
        background: "#fff", borderRadius: 18, padding: "26px 24px",
        border: tier.highlight ? "2px solid #b08a3e" : "1px solid #ecdeb7",
        boxShadow: tier.highlight
          ? "0 20px 44px rgba(176,138,62,.22)"
          : "0 8px 20px rgba(0,0,0,.06)",
        position: "relative",
        display: "flex", flexDirection: "column", gap: 14,
      }}
    >
      {tier.badge && (
        <span style={{
          position: "absolute", top: -12, right: 18,
          background: "linear-gradient(135deg,#1f1f1f 0%,#4a3b1f 50%,#b08a3e 100%)",
          color: "#fff", fontSize: 11, fontWeight: 800,
          padding: "5px 12px", borderRadius: 999,
          letterSpacing: ".05em",
        }}>{tier.badge}</span>
      )}
      <div>
        <div style={{ color: tier.color, fontWeight: 800, letterSpacing: ".12em",
                      fontSize: 12, textTransform: "uppercase" }}>
          {tier.name}
        </div>
        <div style={{ marginTop: 6, fontFamily: "Georgia,serif",
                      fontSize: 40, color: "#1a1408" }}>
          {tier.price}
          <span style={{ fontSize: 15, color: "#8a7a4a", marginLeft: 4 }}>
            {tier.period}
          </span>
        </div>
      </div>
      <p style={{ color: "#5a4a1a", lineHeight: 1.55, fontSize: 14, margin: 0 }}>
        {tier.blurb}
      </p>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
        {tier.features.map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 8, fontSize: 14,
                                 color: "#1a1408", lineHeight: 1.45 }}>
            <Check size={16} color={tier.color} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={tier.cta.href}
        data-testid={`ds-tier-${tier.key}-cta`}
        style={{
          ...ctaPrimaryStyle,
          alignSelf: "flex-start", marginTop: 4,
          background: tier.highlight ? "#b08a3e" : "#c2410c",
          boxShadow: tier.highlight
            ? "0 8px 20px rgba(176,138,62,.32)"
            : "0 8px 20px rgba(194,65,12,.22)",
        }}
      >
        {tier.cta.label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function StatPill({ num, label }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "baseline", gap: 6,
      padding: "8px 14px", borderRadius: 999,
      background: "#fff", border: "1px solid #ecdeb7",
      boxShadow: "0 4px 10px rgba(0,0,0,.05)",
    }}>
      <strong style={{ fontFamily: "Georgia,serif", fontSize: 20, color: "#1a1408" }}>
        {num}
      </strong>
      <span style={{ fontSize: 13, color: "#5a4a1a" }}>{label}</span>
    </div>
  );
}

// ── Print-friendly styles ───────────────────────────────────────────
const PrintStyles = () => (
  <style>{`
    @media print {
      [data-testid="ds-print-btn"], [data-testid="ds-back-plate-btn"],
      [data-hp-founder-chat] { display: none !important; }
      body { background: white !important; }
    }
  `}</style>
);

// ── Dark-mode overrides (iter321) ───────────────────────────────────
// This page uses heavy inline styles (mirrors HustlePlateSalesPage
// aesthetic), so Tailwind `dark:` classes on className can't win against
// element `style=""`. We patch it with a scoped stylesheet using
// html.dark selectors + !important. All colours are stone-palette
// equivalents of the cream / bark / amber tokens used in light mode.
const DarkModeStyles = () => (
  <style>{`
    html.dark [data-testid="hustledirectory-sales-page"] {
      background: #1c1917 !important;
      color: #f5f5f4 !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] h1,
    html.dark [data-testid="hustledirectory-sales-page"] h2,
    html.dark [data-testid="hustledirectory-sales-page"] h3,
    html.dark [data-testid="hustledirectory-sales-page"] strong,
    html.dark [data-testid="hustledirectory-sales-page"] summary {
      color: #f5f5f4 !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] p,
    html.dark [data-testid="hustledirectory-sales-page"] li,
    html.dark [data-testid="hustledirectory-sales-page"] em {
      color: #d6d3d1 !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid^="ds-tier-"],
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid^="ds-uxcard-"],
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid^="ds-step-"],
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid^="ds-faq-"],
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid^="ds-eco-"],
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid="ds-callout-safety"] > div {
      background: #292524 !important;
      border-color: #57534e !important;
      color: #f5f5f4 !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid="ds-stat-pills"] > div {
      background: #292524 !important;
      border-color: #57534e !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid="ds-stat-pills"] strong {
      color: #fcd34d !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid="ds-stat-pills"] span {
      color: #d6d3d1 !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid^="ds-uxcard-"] > div:first-child,
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid^="ds-transparency-"] > div:first-child {
      background: #44403c !important;
      color: #fcd34d !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] [data-testid^="ds-transparency-"] {
      background: #292524 !important;
      border-color: #57534e !important;
      color: #f5f5f4 !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] a[data-testid="ds-cta-add"],
    html.dark [data-testid="hustledirectory-sales-page"] button[data-testid="ds-print-btn"] {
      border-color: #d6d3d1 !important;
      color: #f5f5f4 !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] a[data-testid="ds-cta-add"] svg,
    html.dark [data-testid="hustledirectory-sales-page"] button[data-testid="ds-print-btn"] svg {
      color: #f5f5f4 !important;
    }
    html.dark [data-testid="hustledirectory-sales-page"] em[style*="italic"] {
      color: #fcd34d !important;
    }
  `}</style>
);

// ── Main ────────────────────────────────────────────────────────────
export default function DirectorySalesPage() {
 const [stats,setStats]=useState(null),[unavailable,setUnavailable]=useState(false);
 useEffect(()=>{
  document.title="How it works | HustleDirectory";
  const controller=new AbortController();
  const API=process.env.REACT_APP_BACKEND_URL || window.location.origin;
  fetch(API+"/api/directory/au/overview",{signal:controller.signal})
   .then(r=>{if(!r.ok)throw Error("Totals unavailable");return r.json();})
   .then(data=>{if(!Number.isFinite(data?.totals?.entities)||!Number.isFinite(data?.totals?.suburbs))throw Error("Totals unavailable");setStats(data);})
   .catch(e=>{if(e.name!=="AbortError")setUnavailable(true);});
  return ()=>controller.abort();
 },[]);
 const totalListings=stats?stats.totals.entities.toLocaleString("en-AU"):null;
 const totalSuburbs=stats?stats.totals.suburbs.toLocaleString("en-AU"):null;
 return <div data-testid="hustledirectory-sales-page" style={{background:"#fdf9ec",color:"#1a1408",minHeight:"100vh"}}>
  <PrintStyles/><DarkModeStyles/>
  <PlatformHeader brand={{name:"HustleDirectory",to:"/"}} nav={[{to:"/directory",label:"Browse directory"},{to:"/how-it-works",label:"How it works"},{to:"/claim-business",label:"Claim my listing"}]} showBookCta={false} showCart={false}/>
  <main style={{maxWidth:1200,margin:"0 auto",padding:"24px 24px 80px"}}>
   <a href="/" style={{...ctaSecondaryStyle,marginBottom:20}}>← Back to business search</a>
   <section style={heroStyle} data-testid="ds-hero">
    <p style={{fontSize:12,letterSpacing:".22em",textTransform:"uppercase",color:"#8a6728",fontWeight:800}}>HustleDirectory · Australia</p>
    <h1 style={{fontFamily:"Georgia,serif",fontSize:"clamp(32px,5vw,46px)",lineHeight:1.15,maxWidth:900}}>Help customers find your <em style={{color:"#8a6728"}}>local business.</em></h1>
    <p style={{fontSize:18,lineHeight:1.7,maxWidth:850}}>A useful public profile brings your business name, location, services and contact details together. Find your existing listing, check what customers see and request access to keep it current.</p>
    <p style={{fontSize:16,lineHeight:1.7,maxWidth:850}}>Browse real directory records across Australia. Compare the details available, visit a business website or send an enquiry. Restaurants, cafés, beauty services, trades and other local businesses can be discovered through the same search.</p>
    <div data-testid="ds-stat-pills" style={{display:"flex",gap:14,flexWrap:"wrap",margin:"24px 0"}}>
     {stats?<><StatPill num={totalListings} label="public listings"/><StatPill num={totalSuburbs} label="suburbs geocoded"/></>:<p role="status">{unavailable?"Live totals are temporarily unavailable. Browse the directory to see current listings.":"Loading live directory totals…"}</p>}
    </div>
    <p style={{fontSize:13,maxWidth:850,lineHeight:1.6}}>Maintained database totals, not estimates. Listings include imported records and are not all claimed, verified or independently inspected. Geocoded suburbs have recorded numeric coordinates.</p>
    <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:24}}>
     <a href="/" style={ctaPrimaryStyle}>Find my business <ArrowRight size={16}/></a>
     <Link to="/claim-business" style={ctaSecondaryStyle}>Claim my listing</Link>
     <Link to="/add-business" style={ctaSecondaryStyle}>Add a business</Link>
     <button style={ctaSecondaryStyle} onClick={()=>window.print()}><Printer size={16}/> Print this page</button>
    </div>
   </section>
   <section style={{marginTop:60}} data-testid="ds-public-ux">
    <h2 style={{fontFamily:"Georgia,serif",fontSize:34}}>The details customers need.</h2>
    <div style={{display:"grid",gap:20,gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))"}}>
     {PUBLIC_UX.map((f,i)=><article key={i} data-testid={"ds-uxcard-"+i} style={{background:"white",border:"1px solid #ecdeb7",borderRadius:16,padding:24}}><f.icon size={28} color="#8a6728"/><h3 style={{fontSize:20}}>{f.title}</h3><p style={{lineHeight:1.7}}>{f.body}</p></article>)}
    </div>
   </section>
   <section style={{marginTop:64}} data-testid="ds-steps"><h2 style={{fontFamily:"Georgia,serif",fontSize:34}}>Search → Claim → Maintain.</h2>
    <div style={{display:"grid",gap:20,gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))"}}>{STEPS.map(s=><article key={s.n} data-testid={"ds-step-"+s.n} style={{background:"white",border:"1px solid #ecdeb7",borderRadius:16,padding:24}}><strong style={{fontSize:32,color:"#8a6728"}}>{s.n}</strong><h3>{s.title}</h3><p style={{lineHeight:1.7}}>{s.body}</p></article>)}</div>
   </section>
   <section style={{marginTop:64}} data-testid="ds-tiers"><h2 style={{fontFamily:"Georgia,serif",fontSize:34}}>Start with your listing. Choose more when it fits.</h2><div style={{display:"grid",gap:24,gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))"}}>{TIERS.map(t=><TierCard key={t.key} tier={t}/>)}</div></section>
   <section style={{marginTop:64}} data-testid="ds-suburb-seo"><h2 style={{fontFamily:"Georgia,serif",fontSize:34}}>Public pages built for discovery.</h2><p style={{lineHeight:1.8,maxWidth:900}}>The directory provides public business pages, readable business details, structured data where available and sitemaps for discovery. Accurate information gives visitors a useful place to start. Search engines decide what to index and how to rank it; inclusion does not guarantee traffic, leads or sales.</p></section>
   <section style={{marginTop:64}} data-testid="ds-faqs"><h2 style={{fontFamily:"Georgia,serif",fontSize:34}}>Your questions answered.</h2>{FAQS.map((f,i)=><details key={i} data-testid={"ds-faq-"+i} style={{background:"white",border:"1px solid #ecdeb7",borderRadius:12,padding:20,margin:"12px 0"}}><summary style={{fontWeight:700,cursor:"pointer"}}>{f.q}</summary><p style={{lineHeight:1.7}}>{f.a}</p></details>)}</section>
   <section style={{marginTop:64,padding:28,borderRadius:20,background:"#efe5c7"}}><h2>Check your business listing.</h2><p>Start with a search, then choose the next step for your business.</p><a href="/" style={ctaPrimaryStyle}>Browse the directory <ArrowRight size={16}/></a></section>
  </main><SuiteFooter/>
 </div>;
}
